/**
 * Dialog — the one centered-modal primitive (UX-8, design-system spine).
 *
 * Consolidates the bespoke centered-modal pattern (ConfirmDialog /
 * HandoffDialog / MoveStageDialog / NewIntakeForm grew four copies) onto
 * the proven useDialogDismiss behavior: focus-on-open, Esc-close, Tab
 * trap, restore-focus-on-close — plus scrim-click close. Use
 * role="alertdialog" for confirms.
 *
 * Conditional-render pattern (the house style): parents mount the Dialog
 * when open. There is deliberately NO isOpen prop — useDialogDismiss's
 * focus/restore lifecycle is mount-scoped, and an isOpen flag would split
 * that lifecycle across renders.
 *
 * For the mobile slide-up sheet, use BottomSheet; its `centered` mode is
 * superseded by this primitive for new surfaces.
 */

import { type ReactNode } from 'react';
import { useDialogDismiss } from '@/components/primitives/useDialogDismiss';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';

export interface DialogProps {
  title: string;
  onClose: () => void;
  /** alertdialog for confirm/destructive prompts. */
  role?: 'dialog' | 'alertdialog';
  /** xl is the two-column form width (Design Pass 3: the record's
   *  add/edit dialogs — inputs wide enough that an email shows whole). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Right-aligned action bar, a fixed gap below the body whatever the
   *  body ends with (a field, a sentence, an error line); callers add
   *  no bottom margin of their own. Compose <Button> children, the primary
   *  last. When the bar wraps (a narrow screen, large text) the last
   *  child sits on the top line. */
  footer?: ReactNode;
  /** data-testid on the dialog panel. */
  testId?: string;
  /** The id of the box that takes focus on open; the panel itself when
   *  absent or not found. */
  initialFocusId?: string;
  children: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export function Dialog({
  title,
  onClose,
  role = 'dialog',
  size = 'md',
  footer,
  testId,
  initialFocusId,
  children,
}: DialogProps): ReactNode {
  const ref = useDialogDismiss<HTMLDivElement>(onClose, initialFocusId);
  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-ink/30 px-4 py-10 overflow-auto"
      onClick={onClose}
    >
      <div
        ref={ref}
        role={role}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${SIZE_CLASSES[size]} bg-paper rounded-2xl shadow-sh3 border border-sage-800/[8%] p-5 focus:outline-none`}
      >
        <h3 className="font-display text-lg text-sage-800 mb-1">{title}</h3>
        {children}
        {footer && <div className="mt-4 flex flex-wrap-reverse justify-end gap-2">{footer}</div>}
      </div>
    </div>
    </OverlayPortal>
  );
}
